import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import ListScreen from '../../../components/listScreen/ListScreen';
import { VIEW_TYPE_SCREEN } from '../../../components/listScreen/ListScreen';
import { ENTITY_NAME_BACK_OFFICE_USER } from '../../../dao/dbConstants';
import {
  ROUTE_BACK_OFFICE_USER_NEW,
  ROUTE_BACK_OFFICE_USER_EDIT,
} from '../../../constants';
import { deleteRecord, callUIAPI } from '../../../utils/apiUtils';
import { showErrorMessage, showSuccessMessage } from '@/shared/components/toast-system';
import Button from '../../../components/Button';
import Modal from '../../../components/Modal';
import { setSearchParams } from '../../../redux/searchParamsSlice';
import { DEFAULT_PAGE_SIZE, SORT_DIRECTION_ASCENDNG } from '../../../components/dataTable/constants';

import './InviteModal.css';
import '../../pages/firm/Firm.css';

const BackofficeUsers = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const searchParamsFromState = useSelector((state) => state.searchParams.searchParams[ENTITY_NAME_BACK_OFFICE_USER]);

  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const handleRowClick = (row) => {
    navigate(ROUTE_BACK_OFFICE_USER_EDIT + row.id);
  };

  const refreshList = () => {
    const currentParams = searchParamsFromState || {
      pageNumber: 1,
      pageSize: DEFAULT_PAGE_SIZE,
      orderBy: '',
      sortDirection: SORT_DIRECTION_ASCENDNG,
      searchQuery: '',
      filterValues: {}
    };
    dispatch(setSearchParams({
      entityName: ENTITY_NAME_BACK_OFFICE_USER,
      searchParams: { ...currentParams, pageNumber: 1 }
    }));
  };

  const openDeleteDialog = (event, row) => {
    event.stopPropagation();
    setDeleteTarget(row);
    setDeleteConfirmText('');
    setDeleteError(null);
    setShowDeleteDialog(true);
  };

  const closeDeleteDialog = () => {
    if (deleting) return;
    setShowDeleteDialog(false);
    setDeleteTarget(null);
    setDeleteConfirmText('');
    setDeleteError(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteRecord(ENTITY_NAME_BACK_OFFICE_USER, deleteTarget.id);
      showSuccessMessage(
        `User '${getUserDisplayName(deleteTarget)}' deleted successfully.`
      );
      setShowDeleteDialog(false);
      setDeleteTarget(null);
      setDeleteConfirmText('');
      refreshList();
    } catch (error) {
      let errorMessage = 'Could not delete user.';
      if (error?.response?.data?.isForUser && error.response.data?.message) {
        errorMessage = error.response.data.message;
      }
      setDeleteError(errorMessage);
    } finally {
      setDeleting(false);
    }
  };

  const isDeleteEnabled = deleteConfirmText.trim().toLowerCase() === 'delete';

  const getUserDisplayName = (row) => {
    return (row.firstName + ' ' + (row.lastName || '')).trim();
  };

  const handleUnlock = async (event, row) => {
    event.stopPropagation();
    try {
      await callUIAPI(`bo/backofficeusers/unlock/${row.id}`, null, 'put', true);
      showSuccessMessage(
        `User '${getUserDisplayName(row)}' unlocked successfully.`
      );
      return { ...row, lockoutUntil: null };
    } catch (error) {
      showErrorMessage('Could not unlock user.');
      throw error;
    }
  };

  const handleResendInvite = async (event, row) => {
    event.stopPropagation();
    try {
      await callUIAPI(`bo/backofficeusers/${row.id}/resend-invite`, {}, 'post', true);
      showSuccessMessage(`Invite email resent to ${row.email}.`);
    } catch (error) {
      showErrorMessage('Could not resend invite email.');
      throw error;
    }
  };

  const handleInviteSubmit = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) {
      showErrorMessage('Email address is required.');
      return;
    }

    setIsSubmitting(true);
    try {
      const emailVal = inviteEmail.trim().toLowerCase();
      const emailParts = emailVal.split('@');
      const handle = emailParts[0];
      const rawFirstName = handle.split(/[._-]/)[0] || 'User';
      const cleanFirstName = rawFirstName.charAt(0).toUpperCase() + rawFirstName.slice(1);

      const payload = {
        firstName: cleanFirstName,
        lastName: '',
        email: emailVal,
      };

      await callUIAPI('bo/backofficeusers', payload, 'post', true);
      showSuccessMessage(`Invite sent successfully to ${emailVal}.`);
      setIsInviteModalOpen(false);
      setInviteEmail('');
      refreshList();
    } catch (error) {
      let errorMessage = 'Could not invite user.';
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      showErrorMessage(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const rowActions = [
    {
      label: '',
      class: 'bx bx-lock-open-alt',
      tooltipText: 'Unlock account',
      handler: handleUnlock,
      isDelete: false,
      requireConfirmation: false,
      displayCriteriaFunction: (row) =>
        row.lockoutUntil && new Date(row.lockoutUntil) >= new Date(),
    },
    {
      label: '',
      class: 'bx bx-envelope',
      tooltipText: 'Resend invite',
      handler: handleResendInvite,
      isDelete: false,
      requireConfirmation: false,
    },
    {
      label: '',
      class: 'bx bx-trash',
      tooltipText: 'Delete',
      handler: openDeleteDialog,
      isDelete: false,
      requireConfirmation: false,
    },
  ];

  const customHeaderActions = (
    <Button
      className="primary add"
      onClick={() => setIsInviteModalOpen(true)}
      icon="bx bx-plus"
    >
      Invite Team Member
    </Button>
  );

  return (
    <>
      <ListScreen
        entityName={ENTITY_NAME_BACK_OFFICE_USER}
        heading="Team"
        showAddButton={false}
        addScreen={ROUTE_BACK_OFFICE_USER_NEW}
        editScreen={ROUTE_BACK_OFFICE_USER_EDIT}
        addScreenType={VIEW_TYPE_SCREEN}
        editScreenType={VIEW_TYPE_SCREEN}
        showSearch={true}
        enableRowClick={true}
        onRowClick={handleRowClick}
        rowActions={rowActions}
        customHeaderActions={customHeaderActions}
      />

      {}
      <Modal
        isOpen={isInviteModalOpen}
        onClose={() => {
          if (!isSubmitting) {
            setIsInviteModalOpen(false);
            setInviteEmail('');
          }
        }}
        title="Invite Team Member"
        showTitleBar={true}
      >
        <form 
          onSubmit={handleInviteSubmit} 
          style={{ padding: '20px', width: '100%', boxSizing: 'border-box' }}
        >
          <p style={{ marginBottom: '20px', color: '#555', lineHeight: '1.5', fontSize: '0.95rem' }}>
            Enter the email address of the new team member below. They will receive an email invitation to set up their backoffice account.
          </p>
          
          <div className="input-control separate-label" style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '0.9rem' }}>
              Email Address <span className="required">*</span>
            </label>
            <input
              type="email"
              placeholder="e.g. name@company.com"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              required
              disabled={isSubmitting}
              style={{
                width: '100%',
                padding: '10px 12px',
                fontSize: '0.95rem',
                borderRadius: '5px',
                border: '1px solid #ccc',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div className="buttons" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
            <Button
              type="button"
              className="plain"
              onClick={() => {
                setIsInviteModalOpen(false);
                setInviteEmail('');
              }}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Inviting...' : 'Send Invite'}
            </Button>
          </div>
        </form>
      </Modal>

      {}
      {showDeleteDialog && deleteTarget && (
        <div className="firm-overlay" onClick={closeDeleteDialog}>
          <div className="firm-dialog" onClick={(e) => e.stopPropagation()}>
            {deleting ? (
              <div className="firm-deleting-container">
                <div className="firm-spinner" />
                <p className="firm-deleting-text">Deleting user…</p>
                <p className="firm-deleting-subtext">This may take a moment.</p>
              </div>
            ) : (
              <>
                <div className="firm-dialog-icon">⚠</div>
                <h3 className="firm-dialog-title">
                  Delete &ldquo;{getUserDisplayName(deleteTarget)}&rdquo;?
                </h3>
                <p className="firm-dialog-body">
                  This will <strong>permanently delete</strong> the user{' '}
                  <strong>{deleteTarget.email}</strong> and remove all associated
                  data. This cannot be reversed.
                </p>

                {deleteError && (
                  <p className="firm-dialog-error">{deleteError}</p>
                )}

                <label className="firm-confirm-label">
                  Type <strong>delete</strong> to confirm
                </label>
                <input
                  className="firm-confirm-input"
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="delete"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && isDeleteEnabled) handleDeleteConfirm();
                    if (e.key === 'Escape') closeDeleteDialog();
                  }}
                />

                <div className="firm-dialog-actions">
                  <button
                    className="firm-btn firm-btn-secondary"
                    onClick={closeDeleteDialog}
                  >
                    Cancel
                  </button>
                  <button
                    className="firm-btn-confirm-delete"
                    disabled={!isDeleteEnabled}
                    onClick={handleDeleteConfirm}
                  >
                    Delete user permanently
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default BackofficeUsers;